import * as fs from 'fs';
import * as path from 'path';
import { formatDeployGateFailure } from './workflow-prompts';

export const WORKFLOW_STATE_FILE = '.cfm-agent-state.json';

export interface AgentWorkflowState {
  version: 1;
  coreVerifyPassedAt: string | null;
  deployConfirmed: boolean | null;
  distributionS3Key: string | null;
}

function statePath(projectRoot: string): string {
  return path.join(projectRoot, WORKFLOW_STATE_FILE);
}

export function isWorkflowGateSkipped(): boolean {
  return process.env.CFM_SKIP_WORKFLOW_GATE === '1';
}

export function readWorkflowState(projectRoot: string): AgentWorkflowState | null {
  const file = statePath(projectRoot);
  if (!fs.existsSync(file)) return null;
  try {
    const raw = JSON.parse(fs.readFileSync(file, 'utf8')) as AgentWorkflowState & {
      input3?: unknown;
    };
    if (raw.version !== 1) return null;
    return {
      version: 1,
      coreVerifyPassedAt: raw.coreVerifyPassedAt ?? null,
      deployConfirmed: raw.deployConfirmed ?? null,
      distributionS3Key: raw.distributionS3Key ?? null,
    };
  } catch {
    return null;
  }
}

function writeWorkflowState(projectRoot: string, state: AgentWorkflowState): void {
  fs.writeFileSync(statePath(projectRoot), `${JSON.stringify(state, null, 2)}\n`, 'utf8');
}

export function writeCoreVerifyPassed(projectRoot: string): void {
  writeWorkflowState(projectRoot, {
    version: 1,
    coreVerifyPassedAt: new Date().toISOString(),
    deployConfirmed: null,
    distributionS3Key: null,
  });
}

export function recordDeployConfirmed(projectRoot: string): AgentWorkflowState {
  const existing = readWorkflowState(projectRoot);
  if (!existing?.coreVerifyPassedAt) {
    throw new Error(
      'Core verify not recorded. Run npx cfm-sdk verify-build successfully before set-deploy-target.',
    );
  }
  const next: AgentWorkflowState = {
    ...existing,
    deployConfirmed: true,
  };
  writeWorkflowState(projectRoot, next);
  return next;
}

export function recordDistributionS3Key(projectRoot: string, s3Key: string): AgentWorkflowState {
  const existing = readWorkflowState(projectRoot);
  if (!existing?.deployConfirmed) {
    throw new Error(formatDeployGateFailure());
  }
  const next: AgentWorkflowState = {
    ...existing,
    distributionS3Key: s3Key.trim(),
  };
  writeWorkflowState(projectRoot, next);
  return next;
}

export function assertDeployAllowed(projectRoot: string): void {
  if (isWorkflowGateSkipped()) return;

  const state = readWorkflowState(projectRoot);
  if (!state?.coreVerifyPassedAt) {
    throw new Error(
      'Core verify not recorded. Run npx cfm-sdk verify-build successfully before deploy.',
    );
  }
  if (state.deployConfirmed !== true) {
    throw new Error(formatDeployGateFailure());
  }
}

export function assertDistributionDeployAllowed(projectRoot: string, s3Key?: string): void {
  if (isWorkflowGateSkipped()) return;

  assertDeployAllowed(projectRoot);
  if (!s3Key?.trim()) {
    throw new Error(
      'Missing --s3-key. Display Step 9 client message and wait for the client to paste their distribution key.',
    );
  }
}
